import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faUserMd, faEdit, faTrash, faCalendarAlt, faClock } from '@fortawesome/free-solid-svg-icons';
import { clinicsService, type Doctor } from '../../services/clinicsService';
import BtnTaratic from '../../components/ui/BtnTaratic';
import ClinicsDoctorModal from './ClinicsDoctorModal';
import { useDeleteConfirmation } from '../../components/ui/DeleteConfirmationProvider';
import { useNotifications } from '../../components/ui/notifications';

const ClinicsDoctorsPage: React.FC = () => {
  const confirmDelete = useDeleteConfirmation();
  const notifications = useNotifications();
  const [doctors, setDoctors] = useState<Doctor[]>([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedDoctor, setSelectedDoctor] = useState<Doctor | null>(null);

  const fetchDoctors = async () => {
    try {
      setLoading(true);
      const res = await clinicsService.getDoctors();
      setDoctors(res.doctors);
    } catch (err) {
      console.error(err);
      alert('Error al cargar doctores');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDoctors();
  }, []);

  const handleAddDoctor = () => {
    setSelectedDoctor(null);
    setIsModalOpen(true);
  };

  const handleEditDoctor = (doc: Doctor) => {
    setSelectedDoctor(doc);
    setIsModalOpen(true);
  };

  const handleDeleteDoctor = async (id: number) => {
    if (!await confirmDelete({ title: 'Eliminar doctor', message: '¿Eliminar este doctor?' })) return;
    try {
      await clinicsService.deleteDoctor(id);
      notifications.success('Doctor eliminado');
      fetchDoctors();
    } catch {
      alert('Error al eliminar doctor');
    }
  };

  return (
    <div data-ui="clinics-doctors-page" className="page-theme-surface page-theme-clinics flex flex-col h-full w-full p-8 gap-8 overflow-y-auto custom-scrollbar relative">
      {/* HEADER & ACTIONS */}
      <div data-ui="clinics-doctors-page-region-001" className="flex flex-wrap items-center justify-between gap-4 relative z-10">
        <div data-ui="clinics-doctors-page-region-002" className="flex flex-col gap-2">
          <h1 className="text-3xl font-bold text-white tracking-wide flex items-center gap-3">
            <div data-ui="clinics-doctors-page-region-003" className="w-10 h-10 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center">
              <FontAwesomeIcon icon={faUserMd} className="text-blue-400 text-lg icon-inner-glow-white" />
            </div>
            Doctores
          </h1>
          <p className="text-white font-medium">
            Gestiona los médicos, sus horarios y vacaciones
          </p>
        </div>
        <BtnTaratic onClick={handleAddDoctor} color="blue" size="md" className="flex items-center gap-2">
          <FontAwesomeIcon icon={faPlus} />
          <span className="whitespace-nowrap">Añadir Doctor</span>
        </BtnTaratic>
      </div>

      {loading ? (
        <div data-ui="clinics-doctors-page-region-004" className="flex justify-center p-12 relative z-10">
          <div data-ui="clinics-doctors-page-region-005" className="w-10 h-10 border-2 border-white/20 border-t-white/80 rounded-full animate-spin"></div>
        </div>
      ) : (
        <div data-ui="clinics-doctors-page-region-006" className="rounded-2xl bg-black/20 backdrop-filter backdrop-blur-xl shadow-inner-glow shadow-inner-glow-black-md drop-shadow-glow-black-md overflow-hidden relative z-10 flex flex-col">
          <div data-ui="clinics-doctors-page-region-007" className="overflow-x-auto">
            <table className="w-full text-xs sm:text-sm text-left border-collapse">
              <thead>
                <tr className="bg-white/5 text-white/60 text-[11px] sm:text-xs uppercase tracking-wider border-b border-white/10">
                  <th className="px-5 py-3 font-semibold">Doctor</th>
                  <th className="px-5 py-3 font-semibold">Contacto</th>
                  <th className="px-5 py-3 font-semibold text-center">Horarios</th>
                  <th className="px-5 py-3 font-semibold text-center">Vacaciones</th>
                  <th className="px-5 py-3 font-semibold text-right">Acciones</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/5">
                {doctors.map(doc => (
                  <tr key={doc.id} className="hover:bg-white/5 transition-colors">
                    <td className="px-5 py-3.5 whitespace-nowrap">
                      <div data-ui="clinics-doctors-page-region-008" className="text-white font-medium text-xs sm:text-sm">{doc.name}</div>
                      <div data-ui="clinics-doctors-page-region-009" className="text-blue-400 text-[11px] font-medium">{doc.specialty || 'Sin especialidad'}</div>
                    </td>
                    <td className="px-5 py-3.5 whitespace-nowrap">
                      <div data-ui="clinics-doctors-page-region-010" className="text-white/80 text-xs sm:text-sm">{doc.phone || '—'}</div>
                      <div data-ui="clinics-doctors-page-region-011" className="text-white/50 text-[11px] max-w-[160px] truncate">{doc.email || '—'}</div>
                    </td>
                    <td className="px-5 py-3.5 text-center whitespace-nowrap">
                      <span data-ui="clinics-doctor-workdays-badge" className="clinics-white-shadow-text inline-flex items-center gap-1.5 bg-blue-500/20 text-blue-300 px-3 py-1 rounded-full border border-blue-500/30 text-[11px] font-medium" title={`${doc.workdays.length} días laborables`}>
                        <FontAwesomeIcon icon={faClock} /> {doc.workdays.length} días
                      </span>
                    </td>
                    <td className="px-5 py-3.5 text-center whitespace-nowrap">
                      {doc.vacations.length > 0 ? (
                        <span className="inline-flex items-center gap-1.5 bg-orange-500/20 text-orange-300 px-3 py-1 rounded-full border border-orange-500/30 text-[11px] font-medium" title={`${doc.vacations.length} periodos vacacionales`}>
                          <FontAwesomeIcon icon={faCalendarAlt} /> {doc.vacations.length} periodos
                        </span>
                      ) : (
                        <span className="text-white/30 text-xs">—</span>
                      )}
                    </td>
                    <td className="px-5 py-3.5 whitespace-nowrap">
                      <div data-ui="clinics-doctors-page-region-012" className="flex justify-end gap-2">
                        <BtnTaratic onClick={() => handleEditDoctor(doc)} color="ghost-dark" size="xs" iconOnly title="Editar">
                          <FontAwesomeIcon icon={faEdit} className="text-white/80 hover:text-white transition-colors text-xs" />
                        </BtnTaratic>
                        <BtnTaratic onClick={() => handleDeleteDoctor(doc.id)} color="ghost-dark" size="xs" iconOnly title="Eliminar">
                          <FontAwesomeIcon icon={faTrash} className="text-red-400/80 hover:text-red-400 transition-colors text-xs" />
                        </BtnTaratic>
                      </div>
                    </td>
                  </tr>
                ))}
                {doctors.length === 0 && (
                  <tr>
                    <td colSpan={5} className="px-5 py-12 text-center text-sm text-white/50">
                      No hay doctores registrados.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {isModalOpen && (
        <ClinicsDoctorModal
          doctor={selectedDoctor}
          onClose={() => setIsModalOpen(false)}
          onSaved={() => {
            setIsModalOpen(false);
            fetchDoctors();
          }}
        />
      )}
    </div>
  );
};

export default ClinicsDoctorsPage;
